import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { tap, catchError } from 'rxjs/operators';
import { Authorization } from '../model/authorization';
import { config } from '../config';
import { ErrorHandlerService } from './errorHandler.service';

import { Router } from '@angular/router';
import { timeout } from 'rxjs/operators';
import { Wait } from '../util/timeout';
import { AuthRoles } from '../model/AuthRoles';
import { stringify } from 'querystring';
import { AuthType } from '../model/authType';
import { PagedList } from '../model/paged-list';
import { BasicAuthorization } from '../model/basic-authorization';
import { ReportAuthorization } from '../model/report-authorization';
import { Visited } from '../model/visited';
import { Cacheable } from 'ngx-cacheable';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {

  lastGetFilteredParameters: any[] = [0, 0, ''];
  lastGetActivesParameters: any[] = [0, 0];
  lastGetWaitingOnLobbyParameters: any[] = [0, 0];

  constructor(
    private http: HttpClient,
    private handleError: ErrorHandlerService, private router: Router) { }

  getById(id: number, viewSensitiveUserData: boolean): Observable<Authorization> {
    let params = new HttpParams()
      .set("viewSensitiveUserData", viewSensitiveUserData.toString())
    const url = `${config.apiUrl}/UserAccessAuthorizations/${id}`;
    return this.http.get<Authorization>(url, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.toastTimeout('Demora na comunicação com servidor ao buscar informações do usuário');
          throw(error);
        }));
  }

  getByUserAndRole(userId: number, role: string): Observable<any> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/UserRole/${userId}/${role}`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          throw(error)
        }));
  }

  getByUser(userId: number): Observable<{ items: Authorization[], totalItems: number } | null> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/FromUser/${userId}`;
    return this.http.get<{ items: Authorization[], totalItems: number }>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of(null);
        }));
  }

  getByRfid(rfid: string): Observable<any> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/GetByRfid/${rfid}`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of([]);
        }));
  }

  addNew(authorization: Authorization): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/UserAccessAuthorizations`, authorization)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          throw (error)
        }));
  }

  update(authorizationId: number, authorization: Authorization): Observable<any> {
    return this.http.put<any>(`${config.apiUrl}/UserAccessAuthorizations/${authorizationId}`, authorization)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...', 'Não foi possivel atualizar a autorização');
          throw (error);
        }));
  }

  delete(id: number): Observable<any> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/${id}`;
    return this.http.delete<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...', 'Não foi possivel remover a autorização');
          return of(false);
        }));
  }

  sendPasswordUpdateEmail(id: number): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/UserAccessAuthorizations/${id}/sendPassUpdateEmail`, undefined)
      .pipe(timeout(60000),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          //this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          throw (error)
        }));
  }

  @Cacheable({
    maxCacheCount: 2000,
    maxAge: 60000,
  })
  getByRole(role: string, viewSensitiveUserData: boolean): Observable<{ totalItems: number, items: Visited[] }> {
    let params = new HttpParams()
      .set("viewSensitiveUserData", viewSensitiveUserData.toString())

    const url = `${config.apiUrl}/UserAccessAuthorizations/Roles/${role}`;
    return this.http.get<any>(url, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of([]);
        }));
  }

  getAllUnitResponsables(): Observable<{ totalItems: number, items: Visited[] }> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/AllUnitResponsables`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of([]);
        }));
  }

  getManagersAndOperators(): Observable<any> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/ManagersAndOperators`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor');
          return of([]);
        }));
  }

  getAllowedUnits(): Observable<{ allowedResidences: number[], allowedCompanies: number[] }> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/Units`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          return of([]);
        }));
  }

  getByResidence(residenceId: number, viewSensitiveUserData: boolean, geolocation: string): Observable<[]> {
    let params = new HttpParams()
      .set("viewSensitiveUserData", viewSensitiveUserData.toString())
      .set("geolocation", geolocation);
    const url = `${config.apiUrl}/UserAccessAuthorizations/Residence/${residenceId}`;
    return this.http.get<any>(url, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          return of([]);
        }));
  }

  getByCompany(companyId: number, viewSensitiveUserData: boolean, geolocation: string): Observable<any> {
    let params = new HttpParams()
      .set("viewSensitiveUserData", viewSensitiveUserData.toString())
      .set("geolocation", geolocation);
    const url = `${config.apiUrl}/UserAccessAuthorizations/Company/${companyId}`;
    return this.http.get<any>(url, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          return of([]);
        }));
  }

  getByCompanySimple(companyId: number, viewSensitiveUserData: boolean, geolocation: string): Observable<any> {
    let params = new HttpParams()
      .set("viewSensitiveUserData", viewSensitiveUserData.toString())
      .set("geolocation", geolocation)
      .set("excludeUserDatails", true.toString());
    const url = `${config.apiUrl}/UserAccessAuthorizations/Company/${companyId}`;
    return this.http.get<any>(url, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          return of([]);
        }));
  }

  getPerUnitFiltered(page: number, options): Observable<[]> {
    let url = `${config.apiUrl}/UserAccessAuthorizations/filtered/${page}`;

    let params = new HttpParams()
      .set("roles", options.roles)
      .set("itemsPerPage", options.itemsPerPage)
      .set("nameDocRfid", options.nameDocRfid)
      .set("building", options.building)
      .set("floor", options.floor)
      .set("unit", options.unit)
      .set("unitNameOrUserName", options.unitNameOrUserName)
      .set("serviceProviderCompany", options.serviceProviderCompany)
      .set("viewSensitiveUserData", options.viewSensitiveUserData)
      .set("geolocation", options.geolocation)
      .set("roleSubTypeIds", options.roleSubTypeIds)

    //console.log(`Loading page ${page} ...`, url);
    return this.http.get<any>(url, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of([]);
        }));
  }

  getBasicFiltered(role: string, page: number, pageAmount: number, options): Observable<[]> {
    let url = `${config.apiUrl}/UserAccessAuthorizations/basic/filtered/${role}/${page}/${pageAmount}`;

    let params = new HttpParams()
      .set("nameDocRfidPlate", options.nameDocRfidPlate)
      .set("updateStatus", options.updateStatus)
      .set("building", options.building)
      .set("floor", options.floor)
      .set("unit", options.unit)
      .set("unitName", options.unitName)
      .set("accessGroupIds", options.accessGroupIds)
      .set("showExpired", options.showExpired)
      .set("onlyInsideBuilding", options.onlyInsideBuilding)
      .set("onlyAbsents", options.onlyAbsents);

    if (this.isRequestRepetitive(this.lastGetFilteredParameters, [page, pageAmount, options])) {
      console.log(`Repetitive page ${page} ...`, url);
    }
    else {
      console.log(`Loading page ${page} ...`, url);
      return this.http.get<any>(url, { params: params })
        .pipe(timeout(Wait),
          tap(resp => resp),
          catchError(error => {
            console.log(error.error);
            return of([]);
          }));
    }
  }

  getBasicByDateRange(page: number, options: {
    itemsPerPage: number,
    registerByNameDoc: string,
    registerStart: string,
    registerEnd: string,
    validPeriodStartsAtStart: string,
    validPeriodStartsAtEnd: string,
    validPeriodEndsAtStart: string,
    validPeriodEndsAtEnd: string,
    residenceId: number,
    companyId: number,
    authRoles: string,
    viewSensitiveUserData: boolean,
    geolocation: string
  }): Observable<PagedList<ReportAuthorization>> {

    let params = new HttpParams()
      .set("itemsPerPage", options.itemsPerPage.toString())
      .set("registerByNameDoc", options.registerByNameDoc)
      .set("registerStart", options.registerStart)
      .set("registerEnd", options.registerEnd)
      .set("validPeriodStartsAtStart", options.validPeriodStartsAtStart)
      .set("validPeriodStartsAtEnd", options.validPeriodStartsAtEnd)
      .set("validPeriodEndsAtStart", options.validPeriodEndsAtStart)
      .set("validPeriodEndsAtEnd", options.validPeriodEndsAtEnd)
      .set("residenceId", options.residenceId.toString())
      .set("companyId", options.companyId.toString())
      .set("authRoles", options.authRoles)
      .set("viewSensitiveUserData", options.viewSensitiveUserData.toString())
      .set("geolocation", options.geolocation)

    const url = `${config.apiUrl}/UserAccessAuthorizations/report/${page}`;

    console.log(`Loading page ${page} ...`, url);
    return this.http.get<any>(url, { params: params})
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          if (error.message == 'Timeout has occurred') {
            this.handleError.toastTimeout('Demora na comunicação com servidor');
          }
          return of(undefined);
        }));
  }

  getBasic(page: number, options: {
    itemsPerPage: number,
    updateStatus: number,
    nameDocRfidPlate: string,
    UserRfid: string,
    plateOrRfid: string,
    building: string,
    floor: string,
    unit: string,
    unitName: string,
    accessGroupIds: string,
    showExpired: boolean,
    onlyInsideBuilding: boolean,
    onlyAbsents: boolean,
    blockedAuthorizations: boolean,
    onlyOnLobby: boolean,
    orderByName: boolean,
    authRoles: string,
    roleSubTypeIds: string
  }): Observable<PagedList<BasicAuthorization> | undefined> {

    let params = new HttpParams()
      .set("itemsPerPage", options.itemsPerPage.toString())
      .set("updateStatus", options.updateStatus.toString())
      .set("nameDocRfidPlate", options.nameDocRfidPlate)
      .set("UserRfid", options.UserRfid)
      .set("plateOrRfid", options.plateOrRfid)
      .set("building", options.building)
      .set("floor", options.floor)
      .set("unit", options.unit)
      .set("unitName", options.unitName)
      .set("accessGroupIds", options.accessGroupIds)
      .set("showExpired", options.showExpired.toString())
      .set("onlyInsideBuilding", options.onlyInsideBuilding.toString())
      .set("onlyAbsents", options.onlyAbsents.toString())
      .set("blockedAuthorizations", options.blockedAuthorizations.toString())
      .set("onlyOnLobby", options.onlyOnLobby.toString())
      .set("authRoles", options.authRoles)
      .set("roleSubTypeIds", options.roleSubTypeIds);

    const url = `${config.apiUrl}/UserAccessAuthorizations/basic/active/${page}`;

    if (this.isRequestRepetitive(this.lastGetActivesParameters, [page, options.itemsPerPage])) {
      console.log(`Repetitive page ${page} ...`, url);
      return of(undefined);
    }
    else {
      //console.log(`Loading page ${page} ...`, url);
      return this.http.get<any>(url, { params: params})
        .pipe(timeout(Wait),
          tap(resp => resp),
          catchError(error => {
            console.log(error.error);
            throw(error);
          }));
    }
  }

  getAuthorizationTypes(): Observable<AuthType[]> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/AuthorizationTypes`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of([]);
        }));
  }

  getAdvertisement(): Observable<any> {
    const url = `${config.apiUrl}/Configuration/Advertisement`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          //return of(error.error);
          throw (error.error);
        }));
  }

  contactMe(): Observable<any> {
    return this.http.get<any>(`${config.apiUrl}/Configuration/ContactMe`)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of(error.error);
        }));
  }

  resetRepetityRequestBlocking() {
    this.lastGetFilteredParameters = [0, 0, ''];
    this.lastGetActivesParameters = [0, 0];
    this.lastGetWaitingOnLobbyParameters = [0, 0];
  }

  isRequestRepetitive(lastParameters: any[], currentParameters: any[]): boolean {
    let areEqual: boolean = true;
    for (let index = 0; index < lastParameters.length; index++) {
      if (lastParameters[index] !== currentParameters[index]) {
        areEqual = false;
      }
      lastParameters[index] = currentParameters[index];
    }
    return areEqual;
  }

  imageAdjust(image: any): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/UserAccessAuthorizations/photo/adjust`, image)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          return of(false);
        }));
  }

  imageAnalyse(image: any): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/UserAccessAuthorizations/photo/verify`, image)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of(false);
        }));
  }

  countUsers(): Observable<any> {
    return this.http.get<any>(`${config.apiUrl}/UserAccessAuthorizations/countUsers`)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          return of([]);
        }));
  }

  lobbyPanel(k: string, panelId: string): Observable<any> {
    return this.http.get<any>(`${config.apiUrl}/UserAccessAuthorizations/lobby/panel?k=${k}&panelId=${panelId}`)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          throw (error.error);
        }));
  }

  @Cacheable({
    maxCacheCount: 2000,
    maxAge: 3600000,
  })
  getAllServiceProviderCompany(): Observable<any> {
    const url = `${config.apiUrl}/UserAccessAuthorizations/ServiceProviderCompany`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          throw (error.error);
        }));
  }

  getReportPeopleInsideEnterprise(options): Observable<[]> {

    let params = new HttpParams()
    .set("startAt", options.startAt)
    .set("endAt", options.endAt)
    .set("nameDocRfid", options.nameDocRfid)
    .set("viewSensitiveUserData", options.viewSensitiveUserData)
    .set("geolocation", options.geolocation)
    return this.http.get<any>(`${config.apiUrl}/UserAccessAuthorizations/peopleInside`, { params: params })
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...', 'Não foi possível obter os dados no servidor');
          throw(error);
        }));
  }

  userInvitesBatch(file): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/UserAccessAuthorizations/UserInvitesBatch`, file)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error.error);
          this.handleError.popupError(error, 'Falha na comunicação com o servidor...');
          throw (error)
        }));
  }

}
